// msgpack.ts — just enough MessagePack to read Digitain's account API.
//
// Their `/sportapi/api/V2/AccountService/*` endpoints answer
// `Content-Type: application/x-msgpack`, not JSON (measured live 2026-08-08). This
// is a READER only — we never encode — and it is deliberately small: the shapes we
// meet are maps, arrays, strings, numbers, booleans, nil and ext(-1) timestamps.
//
// Two behaviours that exist because this parses a THIRD PARTY's bytes on a real
// user's page:
//   * a byte budget — a corrupt or hostile length prefix must not spin the parser
//     over a huge allocation inside the operator's tab;
//   * every failure throws, and every CALLER catches. A history read that cannot
//     be parsed is a feature that does nothing, never a broken sportsbook.

/** Refuse anything larger than this. A 30-bet history page measured ~12KB. */
export const MAX_INPUT_BYTES = 4 * 1024 * 1024;

export type MsgPackValue =
  | null | boolean | number | string
  | MsgPackValue[]
  | { [k: string]: MsgPackValue };

class Reader {
  private p = 0;
  private dv: DataView;
  private td = new TextDecoder();

  constructor(private buf: Uint8Array) {
    this.dv = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
  }

  /** Bounds check before every multi-byte read — a truncated response must throw
   *  here rather than yield silent garbage (DataView would throw anyway; a str
   *  slice would NOT, it would just come back short). */
  private need(n: number): void {
    if (this.p + n > this.buf.length) throw new Error("msgpack: truncated");
  }

  private str(n: number): string {
    this.need(n);
    const s = this.td.decode(this.buf.subarray(this.p, this.p + n));
    this.p += n;
    return s;
  }

  /** ext family. Type -1 is the standard timestamp; we return it as an ISO string
   *  so callers never deal with two date representations. Anything else becomes a
   *  marker rather than an exception — an unknown ext in a field we don't read
   *  must not lose us the whole payload. */
  private ext(n: number): MsgPackValue {
    this.need(n + 1);
    const type = this.dv.getInt8(this.p);
    this.p += 1;
    const start = this.p;
    this.p += n;
    if (type !== -1) return `ext(${type})`;
    const d = new DataView(this.buf.buffer, this.buf.byteOffset + start, n);
    if (n === 4) return new Date(d.getUint32(0) * 1000).toISOString();
    if (n === 8) {
      // 30-bit nanos + 34-bit seconds packed into 8 bytes.
      const hi = d.getUint32(0), lo = d.getUint32(4);
      const secs = Number((BigInt(hi & 0x3) << 32n) | BigInt(lo >>> 0));
      return new Date(secs * 1000).toISOString();
    }
    if (n === 12) return new Date(Number(d.getBigInt64(4)) * 1000).toISOString();
    return `ext(${type})`;
  }

  read(): MsgPackValue {
    this.need(1);
    const b = this.buf[this.p++];
    if (b < 0x80) return b;                       // positive fixint
    if (b >= 0xe0) return b - 256;                // negative fixint
    if (b >= 0xa0 && b <= 0xbf) return this.str(b - 0xa0);
    if (b >= 0x90 && b <= 0x9f) return this.array(b - 0x90);
    if (b >= 0x80 && b <= 0x8f) return this.map(b - 0x80);
    switch (b) {
      case 0xc0: return null;
      case 0xc2: return false;
      case 0xc3: return true;
      case 0xcc: this.need(1); return this.buf[this.p++];
      case 0xcd: { this.need(2); const v = this.dv.getUint16(this.p); this.p += 2; return v; }
      case 0xce: { this.need(4); const v = this.dv.getUint32(this.p); this.p += 4; return v; }
      case 0xcf: { this.need(8); const v = Number(this.dv.getBigUint64(this.p)); this.p += 8; return v; }
      case 0xd0: { this.need(1); const v = this.dv.getInt8(this.p); this.p += 1; return v; }
      case 0xd1: { this.need(2); const v = this.dv.getInt16(this.p); this.p += 2; return v; }
      case 0xd2: { this.need(4); const v = this.dv.getInt32(this.p); this.p += 4; return v; }
      case 0xd3: { this.need(8); const v = Number(this.dv.getBigInt64(this.p)); this.p += 8; return v; }
      case 0xca: { this.need(4); const v = this.dv.getFloat32(this.p); this.p += 4; return v; }
      case 0xcb: { this.need(8); const v = this.dv.getFloat64(this.p); this.p += 8; return v; }
      case 0xd9: { this.need(1); return this.str(this.buf[this.p++]); }
      case 0xda: { this.need(2); const n = this.dv.getUint16(this.p); this.p += 2; return this.str(n); }
      case 0xdb: { this.need(4); const n = this.dv.getUint32(this.p); this.p += 4; return this.str(n); }
      case 0xdc: { this.need(2); const n = this.dv.getUint16(this.p); this.p += 2; return this.array(n); }
      case 0xdd: { this.need(4); const n = this.dv.getUint32(this.p); this.p += 4; return this.array(n); }
      case 0xde: { this.need(2); const n = this.dv.getUint16(this.p); this.p += 2; return this.map(n); }
      case 0xdf: { this.need(4); const n = this.dv.getUint32(this.p); this.p += 4; return this.map(n); }
      case 0xd4: return this.ext(1);
      case 0xd5: return this.ext(2);
      case 0xd6: return this.ext(4);
      case 0xd7: return this.ext(8);
      case 0xd8: return this.ext(16);
      case 0xc7: { this.need(1); return this.ext(this.buf[this.p++]); }
      case 0xc8: { this.need(2); const n = this.dv.getUint16(this.p); this.p += 2; return this.ext(n); }
      case 0xc9: { this.need(4); const n = this.dv.getUint32(this.p); this.p += 4; return this.ext(n); }
      case 0xc4: { this.need(1); const n = this.buf[this.p++]; this.need(n); this.p += n; return `bin[${n}]`; }
      case 0xc5: { this.need(2); const n = this.dv.getUint16(this.p); this.p += 2; this.need(n); this.p += n; return `bin[${n}]`; }
      case 0xc6: { this.need(4); const n = this.dv.getUint32(this.p); this.p += 4; this.need(n); this.p += n; return `bin[${n}]`; }
      default: throw new Error(`msgpack: byte 0x${b.toString(16)} at ${this.p - 1}`);
    }
  }

  private array(n: number): MsgPackValue[] {
    // A length prefix can claim more elements than there are bytes to hold them;
    // one byte is the smallest possible element, so this is a cheap sanity bound.
    if (this.p + n > this.buf.length) throw new Error("msgpack: array length overruns input");
    const out: MsgPackValue[] = [];
    for (let i = 0; i < n; i++) out.push(this.read());
    return out;
  }

  private map(n: number): { [k: string]: MsgPackValue } {
    if (this.p + n * 2 > this.buf.length) throw new Error("msgpack: map length overruns input");
    const out: { [k: string]: MsgPackValue } = {};
    for (let i = 0; i < n; i++) {
      const k = this.read();
      out[String(k)] = this.read();
    }
    return out;
  }
}

/** Decode one MessagePack value. Throws on malformed or oversized input. */
export function decodeMsgPack(bytes: Uint8Array): MsgPackValue {
  if (bytes.length > MAX_INPUT_BYTES) throw new Error("msgpack: input too large");
  return new Reader(bytes).read();
}
